import {
  registerAllowCookieMessageHandler
} from "../iframe-communication/message-handlers/seltmannCmsAllowCookie";
import {userHasConsented} from "../cookie-banner/cookieConsent";

let gtag = window.gtag ?? undefined;
let hasInitializedPostLoadSetup = false;
let hasInitializedTag = false;
let hasInitializedTagConfig = false;
let hasPushedOrderVariables = false;

function onSiteAccess(googleAdsConversionId) {
  // is actually already checked for earlier but just in case to avoid accidentally exposing a non-consenting user to Google
  if(!userHasConsented('google-ads-conversion')) {
    return;
  }
  const googleAdsConversionLabelSiteAccess = document.body.dataset.googleAdsConversionLabelSiteAccess ?? null;
  if(googleAdsConversionLabelSiteAccess === null) {
    console.debug("[google-ads-conversion-tracking] no conversion tracking label for page access configured, skipping");
    return;
  }
  console.debug(`[google-ads-conversion-tracking] notifying google that we visited the page. Google Ads Conversion ID: ${googleAdsConversionId}, Conversion label: ${googleAdsConversionLabelSiteAccess}`);
  window.gtag('event', 'conversion', { 'send_to': `AW-${googleAdsConversionId}/${googleAdsConversionLabelSiteAccess}`});
}

function gtagReportPhoneConversion(url) {
  // is actually already checked for earlier but just in case a user changes their mind
  if(!userHasConsented('google-ads-conversion')) {
    return;
  }
  if(typeof(window.gtag) === 'undefined') {
    return;
  }
  const googleAdsConversionId = document.body.dataset.googleAdsConversionId ?? null;
  const googleAdsConversionLabelPhone = document.body.dataset.googleAdsConversionLabelPhone ?? null;
  if(googleAdsConversionId === null || googleAdsConversionLabelPhone === null) {
    return;
  }

  const callback = () => {
    if (typeof(url) !== 'undefined') {
      window.location = url;
    }
  };
  window.gtag('event', 'conversion', { 'send_to': `AW-${googleAdsConversionId}/${googleAdsConversionLabelPhone}`, 'event_callback': callback });
  return false;
}

function setUpPhoneConversionTracking(scope) {
  if(!userHasConsented('google-ads-conversion')) {
    return;
  }
  Array.from(scope.querySelectorAll("a[href^='tel:']")).forEach(aTag => aTag.addEventListener("click", (e) => {
    return gtagReportPhoneConversion(e.currentTarget.href);
  }));
}

function readOrderConversionData(orderConversionTag) {
  const orderData = {
    orderValue: Number(orderConversionTag.dataset.orderValue ?? 1.0),
    orderCurrency: orderConversionTag.dataset.orderCurrency ?? "EUR",
    transactionId: orderConversionTag.dataset.transactionId ?? "",
    newCustomer: null
  };
  if(typeof orderConversionTag.dataset.newCustomer !== 'undefined') {
    orderData.newCustomer = orderConversionTag.dataset.newCustomer === "true";
  }
  return orderData;
}

function gtagReportPurchase(reportObject) {
  // is actually already checked for earlier but just in case to avoid accidentally exposing a non-consenting user to Google
  if(!userHasConsented('google-ads-conversion')) {
    return;
  }

  const googleAdsConversionId = document.body.dataset.googleAdsConversionId ?? null;
  const googleAdsConversionLabelOrder = document.body.dataset.googleAdsConversionLabelOrder ?? null;
  const orderData = readOrderConversionData(reportObject);

  const conversionReport = {
    'send_to': `AW-${googleAdsConversionId}/${googleAdsConversionLabelOrder}`,
    'value': orderData.orderValue,
    'currency': orderData.orderCurrency,
    'transaction_id': orderData.transactionId
  };
  // only report new_customer when the tag actually provides it; an explicit false would make every order count as a returning customer
  if(orderData.newCustomer !== null) {
    conversionReport['new_customer'] = orderData.newCustomer;
  }

  window.gtag('event', 'conversion', conversionReport);
}

function pushOrderVariablesToDataLayer(scope) {
  if(hasPushedOrderVariables) {
    return;
  }
  if(!userHasConsented('google-ads-conversion')) {
    return;
  }
  const orderConversionTag = scope.querySelector(".order-complete-google-conversion");
  if(orderConversionTag === null) {
    return;
  }
  if(typeof window.dataLayer === 'undefined' || typeof window.dataLayer.push !== 'function') {
    console.debug("[google-ads-conversion-tracking] no dataLayer to push order variables to, skipping");
    return;
  }
  const orderData = readOrderConversionData(orderConversionTag);
  const orderVariables = {
    orderValue: orderData.orderValue,
    orderCurrency: orderData.orderCurrency,
    transactionId: orderData.transactionId
  };
  if(orderData.newCustomer !== null) {
    orderVariables.newCustomer = orderData.newCustomer;
  }
  console.debug("[google-ads-conversion-tracking] pushing order variables to dataLayer", orderVariables);
  window.dataLayer.push(orderVariables);
  hasPushedOrderVariables = true;
}

function reportOrderConversion(scope) {
  const orderConversionTag = scope.querySelector(".order-complete-google-conversion");
  if(orderConversionTag === null) {
    return;
  }
  console.debug("[google-ads-conversion-tracking] order complete tag found, reporting purchase conversion");
  gtagReportPurchase(orderConversionTag);
}

function setUpContactFormConversionTracking(scope) {
  if(!userHasConsented('google-ads-conversion')) {
    return;
  }
  scope.querySelectorAll(".form-submit-google-conversion").forEach(conversion => {
    const googleAdsConversionId = document.body.dataset.googleAdsConversionId ?? null;
    const conversionLabel = conversion.dataset.conversionLabel ?? null;
    if(conversionLabel === null) {
      console.warn("[google-ads-conversion-tracking] no conversion label provided for form submission, not counting form.");
      return;
    }
    console.debug("[google-ads-conversion-tracking] conversion tracking for form submission:", { googleAdsConversionId, conversionLabel });
    window.gtag('event', 'conversion', {'send_to': `AW-${googleAdsConversionId}/${conversionLabel}`});
  });
}

function initGoogleTagConfig() {
  const googleAdsConversionId = document.body.dataset.googleAdsConversionId ?? null;
  if(googleAdsConversionId === null) {
    return;
  }
  if(hasInitializedTagConfig) {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  function gtag() { dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('consent', 'default', {
    'ad_user_data': 'denied',
    'ad_personalization': 'denied',
    'ad_storage': 'denied',
    'analytics_storage': 'denied',
    'wait_for_update': 500,
  });
  window.gtag('js', new Date());

  window.gtag('config', `AW-${googleAdsConversionId}`);
  hasInitializedTagConfig = true;
}

function postTagLoadSetup(scope, googleAdsConversionId) {
  initGoogleTagConfig();
  onSiteAccess(googleAdsConversionId);
  setUpPhoneConversionTracking(scope);
  setUpContactFormConversionTracking(scope);
  reportOrderConversion(scope);
  hasInitializedPostLoadSetup = true;
}

function initGoogleTag(scope) {
  const googleAdsConversionId = document.body.dataset.googleAdsConversionId ?? null;
  if(googleAdsConversionId === null) {
    return;
  }
  // is actually already checked for earlier but just in case to avoid accidentally exposing a non-consenting user to Google
  if(!userHasConsented('google-ads-conversion')) {
    return;
  }

  console.debug("[google-ads-conversion-tracking] User has consented to Google Ads Conversion Tracking cookie, initialize Google Tag Manager!");
  window.gtag('consent', 'update', {
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    ad_storage: 'granted',
    analytics_storage: 'granted'
  });
  if(hasInitializedTag) {
    return;
  }
  const url = `https://www.googletagmanager.com/gtag/js?id=AW-${googleAdsConversionId}`;
  const scriptTag = document.createElement("script");
  scriptTag.onload = () => {
    postTagLoadSetup(scope, googleAdsConversionId);
  };
  scriptTag.onerror = (error) => {
    console.error(`[google-ads-conversion] Failed to load Google Ads Conversion Tracking script`, error);
  };
  scriptTag.src = url;
  document.head.appendChild(scriptTag);
  hasInitializedTag = true;
}


function setupGoogleAdsConversionTracking(scope) {
  registerAllowCookieMessageHandler('google-ads-conversion', () => {
    initGoogleTag(scope);
    // late consent on the order complete page: variables were not pushed at setup time because consent was missing
    pushOrderVariablesToDataLayer(scope);
  });
  initGoogleTagConfig();
  pushOrderVariablesToDataLayer(scope);
  if(userHasConsented('google-ads-conversion')) {
    initGoogleTag(scope);
  }
}

export {setupGoogleAdsConversionTracking};
